/**
 * Per-sprint per-audience per-theme gap computation — pure function.
 *
 * Reused by:
 *   - CreateContentStrategyDialog (live preflight panel at 350ms debounce)
 *   - StrategyDraftReviewPage / SprintDraftBlock (per-sprint gap display)
 *   - approveStrategy endpoint guard (under 'strict' policy on the backend)
 *
 * Computes, for each sprint × selected audience pair, how many topics in
 * the backlog actually serve that audience for each themeQuota, vs. how
 * many are needed. Output drives the "Generate N topics" buttons on the
 * review screen.
 */

import type {
  ContentSprint,
  ContentTopic,
  ContentTheme,
  AudienceBriefRef,
} from '@segmently/schema-models/content-plan';
import { resolveAudienceFits } from './resolveAudienceFits';
import { resolveThemeId } from './themeKeys';

export interface SprintAudienceSufficiencyInput {
  sprints: ContentSprint[];
  backlog: ContentTopic[];
  selectedAudienceBriefRefs: AudienceBriefRef[];
  themeCatalog?: Record<string, ContentTheme>;
  /** Topics counted "under" by less than this ratio = gap. Default 0.85. */
  underTolerance?: number;
}

export interface AudienceThemeGap {
  themeId: string;
  needed: number;
  have: number;
  gap: number;
}

export interface SprintAudienceSufficiency {
  sprintId: string;
  /** Sprint's primary audience id; undefined = universal sprint. */
  audienceId?: string;
  themeGaps: AudienceThemeGap[];
  totalGap: number;
  isSufficient: boolean;
}

export interface SprintAudienceSufficiencyOutput {
  perSprint: SprintAudienceSufficiency[];
  allSufficient: boolean;
  estimatedTopicsToGenerate: number;
}

export function checkSprintAudienceSufficiency(
  input: SprintAudienceSufficiencyInput,
): SprintAudienceSufficiencyOutput {
  const {
    sprints,
    backlog,
    selectedAudienceBriefRefs,
    themeCatalog = {},
    underTolerance = 0.85,
  } = input;

  const perSprint: SprintAudienceSufficiency[] = sprints.map((sprint) => {
    const audienceId = (sprint as { primaryAudienceBriefRef?: AudienceBriefRef })
      .primaryAudienceBriefRef?.audienceId;
    const themeQuotas = ((sprint.sprintContract?.outputThemeQuotas as
      | Array<{ themeId?: string; theme?: string; requiredCount: number }>
      | undefined) ?? []);

    const themeGaps: AudienceThemeGap[] = themeQuotas.map((quota) => {
      const themeId = resolveThemeId(quota);
      const requiredCount = quota.requiredCount ?? 0;
      const have = backlog.reduce((sum, topic) => {
        const assigns = (topic.themeAssignment ?? []) as Array<{ themeId?: string; theme?: string; weight?: number }>;
        const themeWeight = assigns.reduce((themeSum, assignment) => {
          if (resolveThemeId(assignment) !== themeId) return themeSum;
          return themeSum + (assignment.weight ?? 1);
        }, 0);
        if (themeWeight <= 0) return sum;
        // No audience constraint → matching is theme-only (universal sprint).
        if (!audienceId) return sum + themeWeight;
        // Universal topic fits any lane.
        if ((topic as { appliesToAllAudiences?: boolean }).appliesToAllAudiences === true) {
          return sum + themeWeight;
        }
        const fits = resolveAudienceFits({
          topic,
          themeCatalog,
          selectedAudienceBriefRefs,
        });
        return fits.some((f) => f.audienceBriefRef?.audienceId === audienceId)
          ? sum + themeWeight
          : sum;
      }, 0);
      const threshold = requiredCount * underTolerance;
      const gap = Math.max(0, Math.ceil(threshold - have));
      return { themeId, needed: requiredCount, have, gap };
    });

    const totalGap = themeGaps.reduce((sum, g) => sum + g.gap, 0);
    const isSufficient = totalGap === 0;
    return { sprintId: sprint.id, audienceId, themeGaps, totalGap, isSufficient };
  });

  const estimatedTopicsToGenerate = perSprint.reduce((sum, s) => sum + s.totalGap, 0);
  const allSufficient = perSprint.every((s) => s.isSufficient);
  return { perSprint, allSufficient, estimatedTopicsToGenerate };
}
